import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Users, BadgeCheck, Search } from "lucide-react";
import { API_URL } from "../config";
import { SPECIALTIES } from "../SpecialtyContext";
import { useAuth } from "../AuthContext";

export default function DoctorDirectory({ onLoginRequest }) {
  const { isLoggedIn, token } = useAuth();
  const [doctors, setDoctors] = useState(null);
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("");

  const load = useCallback(() => {
    setDoctors(null);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (specialty) params.set("specialty", specialty);
    if (!isLoggedIn) { setDoctors([]); return; }
    fetch(`${API_URL}/doctors?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setDoctors)
      .catch(() => setDoctors([]));
  }, [q, specialty, isLoggedIn, token]);

  useEffect(() => {
    const t = setTimeout(load, 300);   // дебаунс поиска по имени
    return () => clearTimeout(t);
  }, [load]);

  if (!isLoggedIn) {
    return (
      <section>
        <h1 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Users size={20} strokeWidth={2.2} color="#e5005b" /> Справочник коллег
        </h1>
        <p style={{ color: "#888", marginTop: -8, marginBottom: 16, fontSize: "0.92rem" }}>
          Справочник коллег доступен зарегистрированным пользователям.
        </p>
        <button type="button" onClick={() => onLoginRequest && onLoginRequest()}>Войти или зарегистрироваться</button>
      </section>
    );
  }

  return (
    <section>
      <h1 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Users size={20} strokeWidth={2.2} color="#e5005b" /> Справочник коллег
      </h1>
      <p style={{ color: "#888", marginTop: -8, marginBottom: 16, fontSize: "0.92rem" }}>
        Найдите коллегу по специальности, чтобы обсудить пациента или передать случай.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", maxWidth: 640 }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search size={15} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#999" }} />
          <input type="text" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Имя врача"
            style={{ width: "100%", padding: "9px 12px 9px 32px", borderRadius: 8, border: "1px solid #c4ceda", fontSize: "0.92rem", boxSizing: "border-box" }} />
        </div>
        <select value={specialty} onChange={e => setSpecialty(e.target.value)}
          style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #c4ceda", fontSize: "0.92rem" }}>
          <option value="">Все специальности</option>
          {SPECIALTIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {doctors === null ? (
        <p style={{ color: "#888" }}>Загрузка…</p>
      ) : doctors.length === 0 ? (
        <p style={{ color: "#888" }}>Никого не нашлось.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
          {doctors.map(d => {
            const specialtyLabels = (d.specialty || "").split(";").map(s => s.trim()).filter(Boolean)
              .map(id => SPECIALTIES.find(s => s.id === id)?.label || id);
            return (
              <Link key={d.id} to={`/doctors/${d.id}`} className="card"
                style={{ textDecoration: "none", color: "inherit", display: "flex", gap: 10, alignItems: "center" }}>
                {d.avatar_url ? (
                  <img src={d.avatar_url} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#007c84", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>
                    {(d.display_name || "?")[0].toUpperCase()}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, fontWeight: 700, color: "#1f2937", fontSize: "0.95rem" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.display_name}</span>
                    {d.verification_status === "verified" && <BadgeCheck size={14} strokeWidth={2.4} color="#15803d" style={{ flexShrink: 0 }} />}
                  </div>
                  <div style={{ color: "#888", fontSize: "0.82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {specialtyLabels.join(", ") || "Специальность не указана"}
                  </div>
                  {d.content_count > 0 && (
                    <div style={{ color: "#e5005b", fontSize: "0.78rem" }}>{d.content_count} публикаций</div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
